// Similar layers: "Select matching layers" and multi-edit (editing one of several similar layers,
// say a grid of cards, edits the same layer in all of them). Layers correspond by their name path
// below each root, with same-named siblings matched in order, as in smart animate.

import type { Document } from './document.ts';
import { newId } from './util.ts';
import type { NodeData, NodeId, Op } from './types.ts';

/** A layer's path below `root`: names with their occurrence among same-named siblings. */
export function pathIn(doc: Document, root: NodeId, id: NodeId): string[] | null {
  const out: string[] = [];
  let n = doc.get(id);
  while (n && n.id !== root) {
    const parent = n.parent;
    if (!parent) return null;
    const same = doc.children(parent).filter((c) => c.name === n!.name);
    out.unshift(`${n.name}#${same.findIndex((c) => c.id === n!.id)}`);
    n = doc.get(parent);
  }
  return n ? out : null;
}

/** The layer at `path` below `root`, if it has one. */
export function atPath(doc: Document, root: NodeId, path: string[]): NodeId | null {
  let id = root;
  for (const step of path) {
    const cut = step.lastIndexOf('#');
    const name = step.slice(0, cut),
      k = Number(step.slice(cut + 1));
    const hit = doc.children(id).filter((c) => c.name === name)[k];
    if (!hit) return null;
    id = hit.id;
  }
  return id;
}

/** The structure of a layer: types and names all the way down (what "similar" compares). */
function shape(doc: Document, n: NodeData, depth = 0): string {
  const kids = depth < 6 ? doc.children(n.id).map((c) => shape(doc, c, depth + 1)) : [];
  return `${n.type}:${n.name}${kids.length ? `(${kids.join(',')})` : ''}`;
}

/** How a layer looks (fills, strokes, effects, corners and text style), for matching unnamed copies. */
function look(n: NodeData): string {
  const text = n.type === 'TEXT' ? [n.fontFamily, n.fontSize, n.fontWeight, n.italic, n.lineHeight, n.letterSpacing] : [Math.round(n.width), Math.round(n.height)];
  return JSON.stringify([n.type, n.fills, n.strokes, n.strokeWeight, n.effects, n.cornerRadius, ...text]);
}

/**
 * Layers matching `id` (Figma's "Select matching layers"): the same type and the same name,
 * component or look, within the same top-level frame (or across the page for top-level layers).
 */
export function matchingLayers(doc: Document, id: NodeId): NodeId[] {
  const n = doc.get(id);
  if (!n) return [];
  const page = doc.pageOf(id);
  if (!page) return [id];
  const top = [n, ...doc.ancestors(id)].find((a) => a.parent === page.id) ?? n;
  const scope = top.id === n.id ? page.id : top.id;
  const sig = look(n);
  const out: NodeId[] = [];
  doc.walk(scope, (c) => {
    if (c.id === scope && scope !== n.id) return;
    if (c.type !== n.type || !c.visible) return;
    const same = (n.componentId && c.componentId === n.componentId) || (!n.componentId && (c.name === n.name || look(c) === sig));
    if (same) out.push(c.id);
  });
  return out.includes(id) ? out : [id, ...out];
}

/** Whether layers can be edited together: two or more of the same type with the same structure. */
export function areSimilar(doc: Document, ids: NodeId[]): boolean {
  const nodes = ids.map((id) => doc.get(id)).filter((n): n is NodeData => !!n);
  if (nodes.length < 2 || nodes.some((n) => doc.isAncestor(nodes[0].id, n.id) || doc.isAncestor(n.id, nodes[0].id))) return false;
  const first = shape(doc, nodes[0]).replace(/^[^:]+:[^(]*/, '');
  return nodes.every((n) => n.type === nodes[0].type && shape(doc, n).replace(/^[^:]+:[^(]*/, '') === first);
}

/**
 * The ops that repeat `ops` (about to be applied) in the other similar roots: property edits,
 * deletions and new layers inside one root happen in the rest at the same place. Edits to the
 * roots themselves aren't repeated (the selection already covers them), and nodes the ops already
 * touch are left alone, so a selection spanning several roots doesn't fight itself.
 */
export function mirrorOps(doc: Document, roots: NodeId[], ops: Op[]): Op[] {
  const live = roots.filter((r) => doc.has(r));
  if (live.length < 2) return [];
  const touched = new Set(ops.map((o) => (o.op === 'create' ? o.node.id : o.id)));
  const rootOf = (id: NodeId) => live.find((r) => r !== id && doc.isAncestor(r, id));
  // nodes created by these ops: their root, path and copies in the other roots
  const made = new Map<NodeId, { root: NodeId; copies: Map<NodeId, NodeId> }>();
  const out: Op[] = [];
  for (const op of ops) {
    if (op.op === 'set') {
      if (op.props.parent !== undefined || op.props.index !== undefined) continue; // moves between parents stay single
      const root = rootOf(op.id);
      if (!root) continue;
      const path = pathIn(doc, root, op.id);
      if (!path) continue;
      for (const other of live) {
        if (other === root) continue;
        const twin = atPath(doc, other, path);
        // renaming changes the path: rename the twin too so they keep matching
        if (twin && !touched.has(twin)) out.push({ op: 'set', id: twin, props: op.props });
      }
    } else if (op.op === 'delete') {
      const root = rootOf(op.id);
      if (!root) continue;
      const path = pathIn(doc, root, op.id);
      if (!path) continue;
      for (const other of live) {
        const twin = other === root ? null : atPath(doc, other, path);
        if (twin && !touched.has(twin)) out.push({ op: 'delete', id: twin });
      }
    } else {
      const parent = op.node.parent;
      if (!parent) continue;
      // a child of a layer made just before: copy alongside that layer's copies
      const madeParent = made.get(parent);
      const root = madeParent?.root ?? (live.includes(parent) ? parent : rootOf(parent));
      if (!root) continue;
      const parentPath = madeParent ? null : pathIn(doc, root, parent);
      const copies = new Map<NodeId, NodeId>();
      for (const other of live) {
        if (other === root) continue;
        const target = madeParent ? madeParent.copies.get(other) : parentPath && atPath(doc, other, parentPath);
        if (!target) continue;
        const id = newId();
        copies.set(other, id);
        // same place among the siblings as the original
        const index = madeParent ? op.node.index : doc.indexAt(target, doc.children(parent).filter((c) => c.index < op.node.index).length);
        out.push({ op: 'create', node: { ...structuredClone(op.node), id, parent: target, index } });
      }
      made.set(op.node.id, { root, copies });
    }
  }
  return out;
}
